import * as Yup from 'yup';
import Swal from 'sweetalert2';

const nullableDate = Yup.date()
  .nullable()
  .transform((value, originalValue) => {
    return originalValue === '' ? null : value;
  });


/**
 * Validation schema for Building Inspection Log
 */
export const BuildingInspectionValidationSchema = Yup.object().shape({
  inspection_date: Yup.date()
    .required('Inspection date is required')
    .typeError('Please select a valid date'),

  property_details: Yup.object({
    property_id: Yup.string().required('Property is required'),
    building_name: Yup.string().required('Building name is required').trim(),
  }).required(),

  interior: Yup.array().of(
    Yup.object({
      name: Yup.string().trim().required('Item name is required'),
      needs_work: Yup.boolean().nullable(),
      comments: Yup.string().trim().default(''),
      attachments: Yup.array().of(Yup.string()).default([]),
      due_date: nullableDate.when('needs_work', {
        is: true,
        then: (schema) =>
          schema.required('Due date is required when work is needed'),
        otherwise: (schema) => schema.nullable(),
      }),
      completed: Yup.boolean().nullable(),
      is_custom: Yup.boolean().default(false),
      memos: Yup.array().of(Yup.object()).optional(),
    })
  ).optional(),

  elevators: Yup.array().of(
    Yup.object({
      name: Yup.string().trim().required('Item name is required'),
      needs_work: Yup.boolean().nullable(),
      comments: Yup.string().trim().default(''),
      attachments: Yup.array().of(Yup.string()).default([]),
      due_date: nullableDate.when('needs_work', {
        is: true,
        then: (schema) =>
          schema.required('Due date is required when work is needed'),
        otherwise: (schema) => schema.nullable(),
      }),
      completed: Yup.boolean().nullable(),
      is_custom: Yup.boolean().default(false),
      memos: Yup.array().of(Yup.object()).optional(),
    })
  ).optional(),

  underground: Yup.array().of(
    Yup.object({
      name: Yup.string().trim().required('Item name is required'),
      needs_work: Yup.boolean().nullable(),
      comments: Yup.string().trim().default(''),
      attachments: Yup.array().of(Yup.string()).default([]),
      due_date: nullableDate.when('needs_work', {
        is: true,
        then: (schema) =>
          schema.required('Due date is required when work is needed'),
        otherwise: (schema) => schema.nullable(),
      }),
      completed: Yup.boolean().nullable(),
      is_custom: Yup.boolean().default(false),
      memos: Yup.array().of(Yup.object()).optional(),
    })
  ).optional(),

  outside: Yup.array().of(
    Yup.object({
      name: Yup.string().trim().required('Item name is required'),
      needs_work: Yup.boolean().nullable(),
      comments: Yup.string().trim().default(''),
      attachments: Yup.array().of(Yup.string()).default([]),
      due_date: nullableDate.when('needs_work', {
        is: true,
        then: (schema) =>
          schema.required('Due date is required when work is needed'),
        otherwise: (schema) => schema.nullable(),
      }),
      completed: Yup.boolean().nullable(),
      is_custom: Yup.boolean().default(false),
      memos: Yup.array().of(Yup.object()).optional(),
    })
  ).optional(),

  misc_others: Yup.array().of(
    Yup.object({
      name: Yup.string().trim().required('Item name is required'),
      comments: Yup.string().trim().default(''),
      attachments: Yup.array().of(Yup.string()).default([]),
      is_custom: Yup.boolean().default(false),
      memos: Yup.array().of(Yup.object()).optional(),
    })
  ).optional(),

  conclusions_recommendations: Yup.string()
    .trim()
    .required('Conclusions & Recommendations are required'),

  status: Yup.string()
    .oneOf(['draft', 'in_review', 'completed', 'archived'])
    .optional(),
  completed_by: Yup.mixed().nullable().optional(),
  finalizedAt: Yup.date().nullable().optional(),
});

/**
 * Make field paths user-friendly for error messages
 */
const prettifyPath = (path) => {
  if (!path) return 'Field';

  const fieldMap = {
    inspection_date: 'Inspection Date',
    'property_details.property_id': 'Property',
    'property_details.building_name': 'Building Name',
    conclusions_recommendations: 'Conclusions & Recommendations',
  };

  if (fieldMap[path]) return fieldMap[path];

  // Array items
  const match = path.match(/^(\w+)\[(\d+)\]\.(.+)$/);
  if (match) {
    const section = match[1];
    const index = Number(match[2]) + 1;
    const field = match[3];

    const sectionName = section
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase());

    if (field === 'name') return `${sectionName} item #${index}`;
    if (field === 'needs_work') return `${sectionName} #${index} - Needs Work`;
    if (field === 'due_date') return `${sectionName} #${index} - Due Date`;
    if (field === 'comments') return `${sectionName} #${index} - Comments`;
    if (field === 'attachments') return `${sectionName} #${index} - Attachments`;
    if (field === 'completed') return `${sectionName} #${index} - Completed`;

    return `${sectionName} #${index} → ${field}`;
  }

  return path
    .split('.')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).replace(/_/g, ' '))
    .join(' → ');
};

/**
 * Group errors into logical sections for better UX
 */
const groupErrors = (errors) => {
  const grouped = {
    General: [],
    Interior: [],
    Elevators: [],
    Underground: [],
    Outside: [],
    'Misc Others': [],
    Summary: [],
  };

  errors.forEach(({ path, message }) => {
    let cleanMessage = message
      .replace('is required', 'is required')
      .replace('Due date is required when work is needed', 'Due date required when flagged as needs work');

    const label = prettifyPath(path);

    if (path.includes('property_details') || path === 'inspection_date') {
      grouped.General.push({ label, message: cleanMessage });
    } else if (path.startsWith('interior')) {
      grouped.Interior.push({ label, message: cleanMessage });
    } else if (path.startsWith('elevators')) {
      grouped.Elevators.push({ label, message: cleanMessage });
    } else if (path.startsWith('underground')) {
      grouped.Underground.push({ label, message: cleanMessage });
    } else if (path.startsWith('outside')) {
      grouped.Outside.push({ label, message: cleanMessage });
    } else if (path.startsWith('misc_others')) {
      grouped['Misc Others'].push({ label, message: cleanMessage });
    } else if (path === 'conclusions_recommendations') {
      grouped.Summary.push({ label, message: cleanMessage });
    } else {
      grouped.General.push({ label, message: cleanMessage });
    }
  });

  return grouped;
};

const namedRows = Yup.array().of(
  Yup.object({
    name: Yup.string().trim().required('Item name is required'),
  })
).optional();

/**
 * What a *draft* needs: a date, a property and a name on every row. Everything
 * else (due dates, conclusions) is what the inspector is still working on.
 */
export const BuildingInspectionDraftSchema = Yup.object().shape({
  inspection_date: Yup.date()
    .required('Inspection date is required')
    .typeError('Please select a valid date'),
  property_details: Yup.object({
    property_id: Yup.string().required('Property is required'),
    building_name: Yup.string().required('Building name is required').trim(),
  }).required(),
  interior: namedRows,
  elevators: namedRows,
  underground: namedRows,
  outside: namedRows,
  misc_others: namedRows,
});

/**
 * Custom validation with beautiful grouped error display using SweetAlert2
 */
export const validateStep = async (values) => runValidation(BuildingInspectionValidationSchema, values, 'before finalizing');

export const validateDraft = async (values) => runValidation(BuildingInspectionDraftSchema, values, 'before saving the draft');

const runValidation = async (schema, values, stage) => {
  try {
    await schema.validate(values, { abortEarly: false });
    return true;
  } catch (err) {
    const errors = err.inner?.map((e) => ({
      path: e.path || '',
      message: e.message,
    })) || [];

    const groupedErrors = groupErrors(errors);

    let html = `
      <div style="text-align: left; font-family: system-ui, -apple-system, sans-serif; max-width: 780px;">
        <p style="margin-bottom: 1.5rem; font-size: 1.1rem; color: #444;">
          Please fix the following issues ${stage}:
        </p>
        <div style="background:#fffbeb; border:2px solid #fbbf24; border-radius:12px; padding:1.5rem; max-height:520px; overflow-y:auto;">
    `;

    const sectionIcons = {
      General: '🏠',
      Interior: '🚪',
      Elevators: '🛗',
      Underground: '⬇️',
      Outside: '🌳',
      'Misc Others': '📋',
      Summary: '📝',
    };

    Object.entries(groupedErrors).forEach(([section, list]) => {
      if (list.length > 0) {
        const title = section;
        const icon = sectionIcons[section] || '⚠️';
        html += `
          <h4 style="margin:1.4rem 0 0.8rem; font-size:1.05rem; color:#d97706; border-bottom:1px solid #fcd34d; padding-bottom:0.4rem;">
            ${icon} ${title}
          </h4>
        `;
        list.forEach(({ label, message }, idx) => {
          html += `
            <div style="margin:0.6rem 0; color:#444; padding-left:0.5rem; line-height:1.4;">
              <strong>${idx + 1}. ${label}:</strong> ${message}
            </div>
          `;
        });
      }
    });

    html += `
        </div>
        <p style="margin-top:1.5rem; font-size:0.95rem; color:#555; text-align:center;">
          Review each section carefully before trying again.
        </p>
      </div>`;

    await Swal.fire({
      icon: 'warning',
      title: 'Incomplete or Invalid Fields',
      html,
      width: 820,
      confirmButtonText: "Got it – I'll fix them",
      confirmButtonColor: '#f59e0b',
      background: '#fffef5',
      customClass: {
        popup: 'animated fadeIn',
      },
    });

    return false;
  }
};

export default BuildingInspectionValidationSchema;